<template>
  <v-container fluid
               class="pa-0 messages-container">
    <!-- Main layout with sidebar and conversation area -->
    <ErrorAlert :error="error">
    </ErrorAlert>
    <v-row no-gutters
           class="fill-height">
      <!-- Conversations sidebar -->
      <v-col cols="12"
             sm="4"
             md="3"
             class="conversations-sidebar">
        <v-card flat
                height="100%"
                class="d-flex flex-column">
          <!-- Header with title and new message button -->
          <v-card-title class="sidebar-header px-4 py-3">
            <span class="text-h6">
              Conversations
            </span>
            <v-spacer></v-spacer>
            <v-btn icon
                   @click="startNewConversation">
              <v-icon>
                fa-edit
              </v-icon>
            </v-btn>
          </v-card-title>

          <!-- Search bar -->
          <v-card-text class="pa-2">
            <v-text-field v-model="searchQuery"
                          dense
                          outlined
                          hide-details
                          prepend-inner-icon="fa-search"
                          placeholder="Search"
                          class="rounded-pill">
            </v-text-field>
          </v-card-text>

          <!-- Conversation list -->
          <v-list class="conversation-list flex-grow-1 overflow-y-auto">
            <v-list-item-group v-model="selectedConversationIndex"
                               color="primary">
              <v-list-item v-for="(conversation, index) in filteredConversations"
                           :key="conversation.id"
                           @click.prevent="selectConversation(conversation.id, index)"
                           :class="{'unread': conversation.unread}">
                <v-list-item-avatar>
                  <v-avatar color="primary">
                    <span class="white--text">
                      {{ conversation.contact?.initials }}
                    </span>
                  </v-avatar>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="conversation-name">
                    {{ conversation.contact?.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="conversation.contact?.formatted_phone"
                                        class="conversation-preview text-truncate">
                    {{ conversation.contact?.formatted_phone }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>
      </v-col>

      <!-- Message area -->
      <v-col cols="12"
             sm="8"
             md="9"
             class="message-area">
        <v-card flat
                height="100%"
                class="d-flex flex-column">
          <template v-if="currentConversation">
            <!-- Conversation header with recipient info -->
            <v-card-title class="conversation-header px-4 py-3">
              <v-avatar color="primary"
                        class="mr-3">
                <span class="white--text">
                  {{ currentConversation.contact?.initials }}
                </span>
              </v-avatar>
              <div>
                <div class="text-h6">
                  {{ currentConversation.contact?.name }}
                </div>
                <div class="text-caption text-subtitle-2">
                  {{ currentConversation.contact?.formatted_phone }}
                </div>
                <div v-if="1===2"
                     class="text-caption text-subtitle-2">
                  {{ currentConversation.status === 'online' ? 'Online' : 'Last seen ' + formatLastSeen(currentConversation.last_seen) }}
                </div>
              </div>

              <v-spacer></v-spacer>

              <template v-if="1===2">
                <v-btn icon>
                  <v-icon>fa-phone</v-icon>
                </v-btn>
                <v-btn icon>
                  <v-icon>fa-video</v-icon>
                </v-btn>
              </template>

              <v-btn icon>
                <v-icon>fa-info-circle</v-icon>
              </v-btn>
            </v-card-title>

            <!-- Messages content -->
            <v-card-text class="messages-content flex-grow-1 overflow-y-auto pa-4 d-flex flex-column"
                         ref="messagesContent">
              <div v-if="currentMessages.length > 0"
                   class="message-date-divider">
                {{ formatMessageDate(currentMessages[0]?.timestamp) }}
              </div>

              <div v-for="(message, index) in currentMessages"
                   :key="message.id"
                   class="message-wrapper mb-5">
                <!-- Date divider if day changes -->
                <div v-if="showDateDivider(message, index)"
                     class="message-date-divider">
                  {{ formatMessageDate(message.timestamp) }}
                </div>

                <!-- Message bubble -->
                <div class="message-bubble-wrapper"
                     :class="{
                    'outgoing': message.sender === 'self',
                    'incoming': message.sender !== 'self',
                    'message-sequence': isPartOfSequence(message, index)}">
                  <div class="message-bubble"
                       :class="{
                    'primary': message.sender === 'self',
                    'grey lighten-3': message.sender !== 'self'}">
                    <div v-html="formatMessageText(message.text)">
                    </div>
                    <span class="message-time caption">
                      {{ formatMessageTime(message.timestamp) }}
                      <v-icon v-if="message.sender === 'self'"
                              x-small
                              class="ml-1">
                        {{ message.status === 'read' ? 'fa-check-double' : 'fa-check' }}
                      </v-icon>
                    </span>
                  </div>
                </div>
              </div>
            </v-card-text>

            <!-- Message input area -->
            <v-card-actions class="message-input-area pa-3">
              <v-btn icon
                     small
                     class="mr-1"
                     @click="openTemplateDialog"
                     color="primary">
                <v-icon>
                  fa-plus-circle
                </v-icon>
              </v-btn>

              <v-textarea v-if="user.can_write_custom_text_messages"
                          v-model="form.content"
                          outlined
                          dense
                          hide-details
                          auto-grow
                          rows="1"
                          row-height="20"
                          :disabled="!!template"
                          placeholder="Text Message - SMS"
                          class="message-input mx-2"
                          @keydown.enter.prevent="sendMessage">
              </v-textarea>
              <v-textarea v-else
                          v-model="form.content"
                          outlined
                          dense
                          hide-details
                          auto-grow
                          rows="1"
                          row-height="20"
                          readonly
                          placeholder="Text Message - Select a template"
                          class="message-input mx-2"
                          @click.prevent="openTemplateDialog">
              </v-textarea>

              <v-btn icon
                     small
                     :color="form.content ? 'success' : ''"
                     :disabled="!form.content?.trim()"
                     @click="sendMessage">
                <v-icon>
                  fas fa-fw fa-paper-plane
                </v-icon>
              </v-btn>
            </v-card-actions>
          </template>

          <!-- Empty state when no conversation is selected -->
          <div v-else
               class="empty-state d-flex flex-column align-center justify-center fill-height">
            <v-icon size="64"
                    color="grey lighten-1"
                    fa-comments>
            </v-icon>
            <div class="text-h6 mt-4 grey--text text--darken-1">
              Select a conversation to start messaging
            </div>
            <v-btn color="primary"
                   class="mt-4"
                   @click="startNewConversation">
              <v-icon left>
                fa-edit
              </v-icon>
              New Message
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Template Selection Dialog -->
    <SelectSinchTemplateDialog v-model="templateDialog"
                               :templates="templates"
                               :sharable-presentations="sharablePresentations"
                               :recipient-name="currentConversation?.contact?.name"
                               @template-selected="onTemplateSelected"
                               @presentation-selected="onPresentationSelected"
                               @template-cleared="onTemplateCleared"
                               @confirm="onTemplateConfirmed"
                               @close="onTemplateDialogClosed">
    </SelectSinchTemplateDialog>

    <!-- New conversation dialog -->
    <v-dialog v-model="newConversationDialog"
              max-width="500px">
      <v-card>
        <v-toolbar color="primary"
                   dark
                   flat
                   tile>
          <v-toolbar-title>
            Start a New Conversation
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-autocomplete v-model="conversationForm.contact"
                          :items="recipientOptions"
                          item-text="name"
                          item-value="id"
                          label="To:"
                          outlined
                          hide-details
                          clearable
                          return-object
                          class="mb-6">
            <template v-slot:selection="{ item: contact }">
              <v-chip small>
                <v-avatar left
                          size="16">
                  <v-img v-if="contact.avatar"
                         :src="contact.avatar">
                  </v-img>
                  <span v-else class="caption">
                    {{ contact.initials }}
                  </span>
                </v-avatar>
                {{ contact.name }}
              </v-chip>
            </template>
            <template v-slot:item="{ item: contact }">
              <v-list-item-avatar>
                <v-avatar v-if="!contact.avatar"
                          color="primary">
                  <span class="white--text caption">
                    {{ contact.initials }}
                  </span>
                </v-avatar>
                <v-img v-else
                       :src="contact.avatar"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ contact.name }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="contact.role">
                  {{ contact.role }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <template v-if="configuration?.registration_url">
            <div class="mb-6">
              Before sending SMS messages, recipients must first opt-in to receive them. Direct customers to opt-in here:<br/>
              <a :href="configuration.registration_url"
                 @click.prevent="copyRegistrationURL">{{ configuration.registration_url }}</a>
            </div>

            <!-- QR Code Section -->
            <div v-if="configuration.registration_url" class="mt-6 text-center">
              <div class="text-subtitle-2 mb-3 grey--text text--darken-1">
                Scan QR Code to Opt-in
              </div>
              <div class="d-flex justify-center">
                <canvas ref="qrCanvas" class="qr-code-canvas"></canvas>
              </div>
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-spacer></v-spacer>
          <v-btn text
                 @click="closeNewConversationDialog">
            Cancel
          </v-btn>
          <v-btn color="primary"
                 :disabled="!conversationForm.contact"
                 @click="createNewConversation">
            Start
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {Form} from 'vform';
import sinchService from '../../../services/sinchService';
import {find, isArray, isBoolean, isInteger, isObject, isString, orderBy} from 'lodash';
import echo from '../../../plugins/echo';
import ErrorAlert from '../../../components/ErrorAlert.vue';
import SelectSinchTemplateDialog from './SelectSinchTemplateDialog.vue';
import {mapGetters} from 'vuex';
import DOMPurify from 'dompurify';
import clipboardService from '../../../../../ml/services/clipboardService';
import QRCode from 'qrcode';

export default {
  components: {
    ErrorAlert,
    SelectSinchTemplateDialog
  },

  data() {
    return {
      searchQuery: '',
      selectedConversationIndex: null,
      currentConversation: null,
      currentMessages: [],
      newMessage: '',
      conversations: [],
      newConversationDialog: false,
      recipientOptions: [],
      loading: false,
      error: null,
      form: new Form({
        sinch_template_id: null,
        content: '',
      }),
      conversationForm: new Form({
        contact: null,
      }),
      template: null,
      selectedPresentation: null,
      templates: [],
      templateDialog: false,
      configuration: null,
    }
  },

  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'presentations',
    ]),
    filteredConversations() {
      if (!this.searchQuery) {
        return this.conversations
      }

      const query = this.searchQuery.toLowerCase()
      return this.conversations.filter(conv =>
        conv.name.toLowerCase().includes(query) || conv.last_message?.text?.toLowerCase().includes(query)
      )
    },
    sharablePresentations() {
      return this.presentations.filter(p => {
        return p.can_be_shared_text;
      });
    },
  },

  watch: {
    // Auto-scroll to the bottom of messages when conversation changes or new messages arrive
    currentConversation: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    // Generate QR code when dialog opens and configuration is available
    newConversationDialog(isOpen) {
      if (isOpen && this.configuration?.registration_url) {
        this.$nextTick(() => {
          this.generateQRCode();
        });
      }
    }
  },

  created() {
    this.fetchConfiguration();
    this.fetchTemplates();
    this.fetchConversations()
    this.fetchRecipients()
  },

  methods: {
    async fetchConfiguration() {
      try {
        const configurations = await sinchService.getConfigurations();
        if (isArray(configurations) && configurations.length > 0) {
          this.configuration = configurations[0];
        } else {
          this.configuration = null;
        }
      } catch (error) {
      }
    },
    async fetchTemplates() {
      this.templates = await sinchService.getTemplatesForUser();
    },

    async fetchConversations() {
      if (!this.user) {
        return;
      }
      this.loading = true
      this.error = null
      try {
        const conversations = await sinchService.getConversationsForCurrentUser();
        this.conversations = orderBy(conversations, ['updated_at'], ['desc']);
      } catch (e) {
        console.error('Error fetching conversations:', e)
        this.error = e;
      } finally {
        this.loading = false
      }
    },

    async fetchRecipients() {
      this.recipientOptions = await this.$store.dispatch('sinch/fetchContacts');
    },

    clearError() {
      this.error = null
    },

    async fetchConversation(conversationID) {
      this.currentConversation = await sinchService.getConversation(conversationID);
    },

    async fetchConversationMessages(conversationID) {
      const messages = await sinchService.getConversationMessages(conversationID);
      this.currentMessages = orderBy(messages, ['timestamp'], ['asc']);
    },

    async selectConversation(conversationID, index) {
      if (this.currentConversation) {
        this.stopListeningToConversation(this.currentConversation)
      }
      try {
        this.clearError();
        console.log('Selecting conversation:', conversationID);
        this.selectedConversationIndex = index;
        await this.fetchConversation(conversationID);
        await this.fetchConversationMessages(conversationID);
        this.listenForConversationEvents(this.currentConversation);
        this.$nextTick(() => {
          this.scrollToBottom();
        })
      } catch (e) {
        this.currentConversation = null;
        this.currentMessages = [];
        this.selectedConversationIndex = null;
        this.error = e;
      }
    },

    listenForConversationEvents(conversation) {
      echo.private(conversation.channel)
        .listen('Sinch.IncomingSinchReplyEvent', async reply => {
          console.log('Received reply:', reply);
          this.currentMessages.push(reply);
          this.$nextTick(() => {
            this.scrollToBottom();
          })
        });
      console.log('Listening for events on channel:', conversation.channel);
    },

    stopListeningToConversation(conversation) {
      echo.leave(conversation.channel);
      console.log('Stopped listening for events on channel:', conversation.channel);
    },

    async markAsRead(conversationID) {
      console.log('markAsRead', conversationID);
      const conversation = await sinchService.markAsRead(conversationID);
      this.conversations = this.conversations.map(c => {
        if (c.id === conversation.id) {
          c = conversation;
        }
        return c;
      });
    },

    async sendMessage() {
      if (!this.currentConversation || !this.form.content) {
        return
      }

      try {
        await this.form.post(`/api/sinch/conversations/${this.currentConversation.id}/messages`)
        await this.fetchConversationMessages(this.currentConversation.id);
        this.$nextTick(() => {
          this.form.reset();
          this.scrollToBottom()
        })
      } catch (e) {
        this.error = e;
      }
    },

    closeNewConversationDialog() {
      this.newConversationDialog = false
      this.conversationForm.reset();
    },

    startNewConversation() {
      this.conversationForm.reset();
      this.newConversationDialog = true
    },

    async createNewConversation() {
      if (isObject(this.conversationForm.contact) === false) {
        return
      }
      if (isInteger(this.conversationForm.contact.id) === false) {
        return;
      }
      const contactID = this.conversationForm.contact.id;
      let conversation = find(this.conversations, c => c.sinch_contact_id === contactID);
      if (!conversation) {
        const form = {
          sinch_contact_id: contactID,
        }
        conversation = await sinchService.createConversation(form);
        this.conversations.unshift(conversation);
      }
      this.closeNewConversationDialog();
      const index = 0;
      await this.selectConversation(conversation.id, index);
    },

    formatDate(timestamp) {
      if (!timestamp) {
        return ''
      }

      const dateObj = new Date(timestamp * 1000);
      const now = new Date()
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)

      const dateDay = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate())
      const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterdayDay = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate())

      if (dateDay.getTime() === nowDay.getTime()) {
        return dateObj.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})
      } else if (dateDay.getTime() === yesterdayDay.getTime()) {
        return 'Yesterday'
      } else {
        return dateObj.toLocaleDateString([], {month: 'numeric', day: 'numeric', year: 'numeric'})
      }
    },

    formatLastSeen(timestamp) {
      if (!timestamp) {
        return 'a while ago'
      }

      const dateObj = new Date(timestamp * 1000)
      const now = new Date()
      const diff = now - dateObj

      if (diff < 60000) {
        return 'just now'
      }

      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000)
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
      }

      if (dateObj.toDateString() === now.toDateString()) {
        return `today at ${dateObj.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`
      }

      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (dateObj.toDateString() === yesterday.toDateString()) {
        return `yesterday at ${dateObj.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`
      }

      return dateObj.toLocaleDateString([], {month: 'short', day: 'numeric', year: 'numeric'})
    },

    formatMessageText(text) {
      if (isString(text)) {
        return DOMPurify.sanitize(text.replace(/\r\n|\r|\n/g, '<br/>'));
      }
      return text
    },

    formatMessageDate(timestamp) {
      if (!timestamp) {
        return ''
      }

      const dateObj = new Date(timestamp * 1000)
      const now = new Date()
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)

      if (dateObj.toDateString() === now.toDateString()) {
        return 'Today'
      } else if (dateObj.toDateString() === yesterday.toDateString()) {
        return 'Yesterday'
      } else {
        return dateObj.toLocaleDateString([], {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      }
    },

    formatMessageTime(timestamp) {
      if (!timestamp) {
        return ''
      }

      const dateObj = new Date(timestamp * 1000)
      return dateObj.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})
    },

    showDateDivider(message, index) {
      if (index === 0) return false

      const prevMessage = this.currentMessages[index - 1]
      if (!prevMessage) return false

      const msgDate = new Date(message.timestamp)
      const prevDate = new Date(prevMessage.timestamp)

      return msgDate.toDateString() !== prevDate.toDateString()
    },

    isPartOfSequence(message, index) {
      if (index === 0) return false

      const prevMessage = this.currentMessages[index - 1]
      if (!prevMessage) return false

      return (
        message.sender === prevMessage.sender &&
        Math.abs(message.timestamp - prevMessage.timestamp) < 5 * 60 * 1000
      )
    },

    scrollToBottom() {
      const container = this.$refs.messagesContent
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // Template dialog event handlers
    onTemplateSelected(template) {
      this.template = template;
      this.form.sinch_template_id = template.id;

      if (isObject(template) && isBoolean(template.has_shared_link_tag)) {
        console.log('Selected template', template.id, template.name, template.has_shared_link_tag);
        if (template.has_shared_link_tag === false) {
          this.renderTemplate(template.id, null);
          this.closeTemplateDialog();
        }
      }
    },

    onPresentationSelected(presentation) {
      this.selectedPresentation = presentation;
    },

    onTemplateCleared() {
      this.clearTemplate();
    },

    onTemplateConfirmed(selection) {
      if (selection.template?.has_shared_link_tag && selection.presentation) {
        this.renderTemplate(selection.template.id, selection.presentation.id);
      }
      this.closeTemplateDialog();
    },

    onTemplateDialogClosed() {
      this.templateDialog = false;
    },

    async renderTemplate(templateID, presentationID = null) {
      try {
        console.log('renderTemplate START', templateID, presentationID);
        this.form.startProcessing();
        const sinchContactID = this.currentConversation.sinch_contact_id;
        this.form.content = await sinchService.renderTemplate(templateID, sinchContactID, presentationID);
        this.form.finishProcessing();
        console.log('renderTemplate END', templateID, presentationID);
      } catch (error) {
        this.form.handleErrors(error);
        this.error = error;
        console.error('renderTemplate ERROR:', error);
      } finally {
        this.form.busy = false;
      }
    },

    openTemplateDialog() {
      this.clearTemplate();
      this.templateDialog = true;
    },

    closeTemplateDialog() {
      this.templateDialog = false;
    },

    clearTemplate() {
      this.form.sinch_template_id = null;
      this.template = null;
      this.selectedPresentation = null;
    },

    showEditMessageAlert() {
      if (this.user?.can_write_custom_text_messages === false) {
        this.$toast.error('You are not allowed to edit messages. You have to select a template.');
      }
    },

    async copyRegistrationURL() {
      try {
        if (this.configuration?.registration_url) {
          await clipboardService.copy(this.configuration.registration_url);
          this.$toast.success('URL copied to clipboard.');
        }
      } catch (error) {
        this.$toast.error('Failed to copy URL to clipboard.');
      }
    },

    async generateQRCode() {
      try {
        if (this.$refs.qrCanvas && this.configuration?.registration_url) {
          await QRCode.toCanvas(this.$refs.qrCanvas, this.configuration.registration_url, {
            width: 200,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
        }
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.messages-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.conversations-sidebar {
  border-right: 1px solid #e0e0e0;
  height: 100%;
}

.sidebar-header, .conversation-header {
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.conversation-list {
  height: calc(100% - 120px);
}

.unread .conversation-name {
  font-weight: bold;
}

.conversation-meta {
  min-width: 65px;
  text-align: right;
}

.messages-content {
  height: 500px;
  padding-bottom: 16px;
  background-color: #f5f5f5;
}

.message-date-divider {
  text-align: center;
  margin: 16px 0;
  color: #757575;
  font-size: 0.8rem;
}

.message-bubble-wrapper {
  display: flex;
  margin: 8px 0;
  max-width: 80%;
}

.message-bubble {
  padding: 8px 12px;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
}

.message-bubble .message-time {
  position: absolute;
  right: 8px;
  bottom: -18px;
  font-size: 0.7rem;
  opacity: 0.7;
  white-space: nowrap;
}

.outgoing {
  justify-content: end;
  margin-left: auto;
}

.outgoing .message-bubble {
  border-bottom-right-radius: 4px;
  color: white;
}

.incoming {
  margin-right: auto;
}

.incoming .message-bubble {
  border-bottom-left-radius: 4px;
}

.message-sequence.outgoing .message-bubble {
  border-bottom-right-radius: 18px;
}

.message-sequence.incoming .message-bubble {
  border-bottom-left-radius: 18px;
}

.message-input-area {
  border-top: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.message-input {
  max-height: 120px;
}

.empty-state {
  background-color: #f5f5f5;
}

.qr-code-canvas {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
